import { HotTable, HotTableProps } from "@handsontable-pro/react";
import { restrictions, auth, hotel as gnHotel } from "gn-shared";
import React, { Component } from "react";
import { toast } from "react-toastify";
import "./yieldsheet.css";

import {
  IAvailabilityUpdate,
  IRateUpdate,
  IRestrictionsUpdate,
  ISuggestedBaseRateUpdate,
  IManualRateUpdate,
} from "../../models/api.models";
import { YieldApi } from "../../api/yield";
import { CellCoords, IYieldDay } from "../../models/yield.models";
import { rowType, Rowprop } from "./rowProp";
import {
  availabilityExceedsTotalRooms,
  cells,
  columns,
  colWidths,
} from "./yield-sheet.helpers";
import EventTable from "./subcomponents/eventTable/eventTable";
import { getRestrictionsMenuItems } from "./restrictions-menu";

import MatrixModal from "./subcomponents/SuggestionModal/matrixesModal";
import { formatISO, getTime } from "date-fns";
import { YieldSheetService } from "App/services";
import { ACTIONS, hasPermission } from "App/common/permissionHandler";

export interface IYieldSheetProps {
  permissions: any;
  hotel: any;
  onDataUpdate: () => void;
  rowProps: Rowprop;
  data: any;
  sheet: IYieldDay[];
  start: Date;
  end: Date;
  compset: any[];
  compareOtaRate: any;
  compareDemand: any;
  activeHotel: any;
}

interface State {
  hotel: any;
  forceRender: boolean;
  subscribedSource: string[];
}

type CellChange = [number, number, number | string, (number | string)?];

class YieldSheet extends Component<IYieldSheetProps, State> {
  state: State = {
    hotel: {},
    forceRender: false,
    subscribedSource: [],
  };

  private suggestionModalInstanceRef = React.createRef<any>();
  private hotInstanceRef = React.createRef<HotTable>();
  private leftToSellUpdates: any = {};

  constructor(props: any) {
    super(props);
    const hotelObject = JSON.parse(JSON.stringify(this.props.activeHotel));
    this.state.hotel = gnHotel(hotelObject);
  }
  shouldComponentUpdate(nextProps: any, nextState: State) {
    if (
      nextProps.data === this.props.data &&
      nextState.subscribedSource === this.state.subscribedSource
    ) {
      return false;
    }
    return true;
  }

  async componentDidMount() {
    const response = await YieldSheetService.revanista.hotel.getCompSetList(
      this.state.hotel.ota.hotelId,
    );
    this.setState({ subscribedSource: response.subscribedSource });
  }

  openSuggestionModal = () => {
    this.suggestionModalInstanceRef.current.open();
  };

  handleEdit = async (changes: CellChange[]) => {
    if (!changes) {
      return;
    }
    const { sheet } = this.props;
    const rateUpdates: IRateUpdate[] = [];
    const suggestedBaseRateUpdates: ISuggestedBaseRateUpdate[] = [];
    const availabilityUpdates: IAvailabilityUpdate[] = [];
    const manualRateUpdates: IManualRateUpdate[] = [];
    const rowsProps = this.props.rowProps;

    const columnCount = changes[0];
    console.log("changes===", columnCount);
    const day = sheet[(columnCount[1] as number) - 1];
    const todayDate = formatISO(day.dateMidday, { representation: "date" });

    const updateBase = {
      hotelId: this.props.hotel.hotelId,
      start: todayDate,
      end: todayDate,
    };

    changes.forEach((change) => {
      const [row, col, prev, current] = change;
      const rp = rowsProps.get(row);
      const rowtype = rp.type;
      if (row === col && row === 0) {
        return;
      }

      if (current === prev) {
        return;
      }

      const day = sheet[(col as number) - 1];
      const isoDate = formatISO(day.dateMidday, { representation: "date" });

      const updateBase = {
        hotelId: this.props.hotel.hotelId,
        start: isoDate,
        end: isoDate,
      };
      const value = Number(current);

      const rowProp = rowsProps.get(row);
      if (rowProp && rowProp.type === "ratePlan") {
        manualRateUpdates.push({
          ...updateBase,
          rate: value,
          invTypeCode: rowProp.roomTypeId,
          ratePlanID: rowProp.ratePlanId,
        });
      }

      switch (rowtype) {
        case rowType.suggestedBaseRate:
          console.log("value", value);

          suggestedBaseRateUpdates.push({
            ...updateBase,
            suggestedRate: value,
          });
          break;
        case rowType.actualBaseRate: {
          const suggestedBaseRate = sheet[col - 1].suggestedBaseRate;
          // REV-911
          if (suggestedBaseRate === prev) {
            suggestedBaseRateUpdates.push({
              ...updateBase,
              suggestedRate: value,
            });
          }
          rateUpdates.push({
            ...updateBase,
            baseRate: value,
            col: col,
          });
          break;
        }
        case rowType.actualOWSBaseRate:
          rateUpdates.push({
            ...updateBase,
            owsBaseRate: value,
            col: col,
          });
          break;
        case rowType.leftToSellUpdate:
          this.leftToSellUpdates[isoDate] = value;
          break;
      }

      // REV-260
      if (rowtype === rowType.roomType) {
        availabilityUpdates.push({
          ...updateBase,
          availability: value,
          invTypeCode: rp.roomTypeId,
        });
        if (
          availabilityExceedsTotalRooms(this.props.hotel.meta.totalRooms, value)
        ) {
          toast.warn("Availability entered exceeds total number of rooms");
        }
      }
    });

    // REV-260
    if (availabilityUpdates.length) {
      YieldApi.updateAvailability(availabilityUpdates)
        .catch((err: any) => {
          console.error(err);
          toast.error("Error while updating availability");
        })
        .finally(() => {
          this.props.onDataUpdate();
        });
    }

    // Updating base rate
    if (rateUpdates.length && !suggestedBaseRateUpdates.length) {
      await Promise.all(
        rateUpdates.map(async (rateUpdate) => {
          if (columnCount[3] !== "") {
            await YieldApi.updateRates(rateUpdate).catch((err: any) => {
              console.error(err);
              toast.error("Error while updating rates");
            });
          }
        }),
      );
      this.props.onDataUpdate();
    }

    // Updating suggested base rate
    if (suggestedBaseRateUpdates.length && !rateUpdates.length) {
      if (columnCount[3] !== "") {
        if (Number(columnCount[3]) === day.actualBaseRate) {
          await YieldApi.removeSuggestedRate(updateBase).catch((err: any) => {
            console.error(err);
            toast.error("Error while deleting suggested base rates");
          });
        } else {
          await YieldApi.updateSuggestedBaseRate(
            suggestedBaseRateUpdates,
          ).catch((err: any) => {
            console.error(err);
            toast.error("Error while updating suggested base rates");
          });
        }
      }
      this.props.onDataUpdate();
    }

    if (manualRateUpdates.length) {
      await Promise.all(
        manualRateUpdates.map(async (manualRateUpdate) => {
          await YieldApi.writeManualRate(manualRateUpdate).catch((err: any) => {
            console.log(err);
            toast.error("Error while updating manual rate");
          });
        }),
      );
      this.props.onDataUpdate();
    }
  };

  updateRestrictions = async (
    { row, col }: CellCoords,
    restrictionKey: RestrictionTypeKeys,
    action: "apply" | "remove",
    numberOfDays?: number,
  ) => {
    if (numberOfDays === undefined) {
      numberOfDays = 1;
    }

    const rowProp = this.props.rowProps.get(row);

    const day = this.props.sheet[col - 1];
    const isoDate = formatISO(day.dateMidday, { representation: "date" });
    const updateBase = {
      hotelId: this.props.hotel.hotelId,
      start: isoDate,
      end: isoDate,
    };
    const restriction: RestrictionToApply = {
      type: restrictions.TYPE[restrictionKey],
      applies: [
        {
          roomType: rowProp.roomTypeId,
          ratePlans:
            typeof rowProp.ratePlanId !== "undefined"
              ? [rowProp.ratePlanId]
              : undefined,
        },
      ],
      dayCount: numberOfDays,
    };

    const specificRestriction = {
      type: restrictions.TYPE[restrictionKey],
      active: action === "apply",
      applies: [
        {
          roomType: rowProp.roomTypeId,
          ratePlans:
            typeof rowProp.ratePlanId !== "undefined"
              ? [rowProp.ratePlanId]
              : undefined,
        },
      ],
      dayCount: numberOfDays,
    };

    if (action === "apply") {
      await restrictions.apply(day, restriction);
    } else {
      const removePayload: {
        type: string;
        roomType: string;
        ratePlan?: string;
      } = {
        type: restrictions.TYPE[restrictionKey],
        roomType: rowProp.roomTypeId,
        ...(typeof rowProp.ratePlanId !== "undefined"
          ? { ratePlan: rowProp.ratePlanId }
          : {}),
      };
      await restrictions.remove(rowProp.globalRes, day, removePayload);
    }

    const update: IRestrictionsUpdate = {
      ...updateBase,
      ...specificRestriction,
    };

    await YieldApi.writeRestrictions(update).catch((err: any) => {
      console.log(err);
      toast.error("Error while updating restrictions");
    });
  };

  bulkUpdateOwsOverride = async (selection: any, label: string) => {
    let start, end;
    if (selection.length === 1) {
      start = selection[0].start;
      end = selection[0].end;
    }
    if (selection.length > 1) {
      start = selection[0].start;
      end = selection[selection.length - 1].end;
    }

    const startCol = start.col;
    const endCol = end.col;
    const dayStart = this.props.sheet[startCol - 1];
    const dayEnd = this.props.sheet[endCol - 1];
    const startIsoDate = formatISO(dayStart.dateMidday, {
      representation: "date",
    });
    const endIsoDate = formatISO(dayEnd.dateMidday, {
      representation: "date",
    });
    const update = {
      hotelId: this.props.hotel.hotelId,
      start: startIsoDate,
      end: endIsoDate,
      label: label,
    };
    YieldApi.updateOwsOverride(update)
      .catch((err: any) => {
        console.error(err);
        toast.error("Error while setting OWS override");
      })
      .finally(() => {
        this.props.onDataUpdate();
      });
  };

  closeOrOpenDay = async (
    col: number,
    action: "apply" | "remove",
    subCat: string,
  ) => {
    const resType: RestrictionTypeKeys = "STOP_SELL";
    const rowProps = this.props.rowProps;
    const day = this.props.sheet[col - 1];
    const isoDate = formatISO(day.dateMidday, { representation: "date" });

    // For specific channel types, use the bulk update API
    if (["ota", "ows", "gds", "rewards"].includes(subCat)) {
      try {
        // Use the appropriate API based on the channel type
        if (subCat === "rewards") {
          await YieldApi.bulkUpdateRewards({
            hotelId: this.props.hotel.hotelId,
            start: isoDate,
            end: isoDate,
            active: action === "remove", // remove = open, apply = close
          });
        } else {
          // For OTA, OWS, GDS use the regular restrictions API with channel type
          await YieldApi.writeRestrictions({
            hotelId: this.props.hotel.hotelId,
            start: isoDate,
            end: isoDate,
            dow: {
              Mon: true,
              Tue: true,
              Wed: true,
              Thu: true,
              Fri: true,
              Sat: true,
              Sun: true,
            },
            type: "stop_sell",
            active: action === "apply", // apply = close, remove = open
            channelType: subCat.toUpperCase(),
          });
        }
        return this.props.onDataUpdate();
      } catch (error) {
        console.error(
          `Error ${action === "apply" ? "closing" : "opening"} ${subCat}:`,
          error,
        );
        return;
      }
    }

    if (subCat === "all") {
      // Build applies with both room-type-only and room-type-with-all-rateplans
      const roomTypeToPlans = new Map<string, Set<string>>();
      for (let r = 0; r <= rowProps.lastRowIndex; r++) {
        const rp = rowProps.get(r);
        if (!rp || !rp.roomTypeId) continue;
        if (!roomTypeToPlans.has(rp.roomTypeId)) {
          roomTypeToPlans.set(rp.roomTypeId, new Set<string>());
        }
        if (typeof rp.ratePlanId !== "undefined") {
          roomTypeToPlans.get(rp.roomTypeId)!.add(rp.ratePlanId);
        }
      }

      const applies: { roomType: string; ratePlans?: string[] }[] = [];
      for (const [roomType, plansSet] of roomTypeToPlans.entries()) {
        applies.push({ roomType });
        const plans = Array.from(plansSet);
        if (plans.length) {
          applies.push({ roomType, ratePlans: plans });
        }
      }

      await YieldApi.bulkUpdateRestrictions({
        active: action === "apply",
        applies,
        dow: {
          Mon: true,
          Tue: true,
          Wed: true,
          Thu: true,
          Fri: true,
          Sat: true,
          Sun: true,
        },
        end: isoDate,
        hotelId: this.props.hotel.hotelId,
        start: isoDate,
        type: restrictions.TYPE[resType],
      }).catch((err: any) => {
        console.error(err);
        toast.error("Error while updating restrictions");
      });
      return this.props.onDataUpdate();
    }
    return this.props.onDataUpdate();
  };

  minDayForDay = async (col: number, action: "apply" | "remove") => {
    const resType: RestrictionTypeKeys = "MIN_DAYS";
    const numberOfDays = action === "apply" ? 2 : 1;
    const rowProps = this.props.rowProps;
    for (let row = 0; row <= rowProps.lastRowIndex; row++) {
      const rp = rowProps.get(row);
      const restriction: RestrictionToApply = {
        type: restrictions.TYPE[resType],
        applies: [
          {
            roomType: rp.roomTypeId,
            ratePlans:
              typeof rp.ratePlanId !== "undefined"
                ? [rp.ratePlanId]
                : undefined,
          },
        ],
      };
      if (restriction.applies && restriction.applies.length > 0) {
        await this.updateRestrictions(
          { row, col },
          resType,
          action,
          numberOfDays,
        );
      }
    }
    return this.props.onDataUpdate();
  };

  computeLeftToSell = (e: any) => {
    e.stopImmediatePropagation();
    if (Object.keys(this.leftToSellUpdates).length === 0) {
      return;
    }

    if (Object.keys(this.leftToSellUpdates).length < this.props.sheet.length) {
      toast("Left to sell must be updated for all the dates in view", {
        type: "error",
      });
      return;
    }

    // Check if any value in leftToSellUpdates is a decimal number
    for (const key in this.leftToSellUpdates) {
      if (this.leftToSellUpdates.hasOwnProperty(key)) {
        const value = Number(this.leftToSellUpdates[key]);
        if (!Number.isInteger(value)) {
          toast("Left to sell cannot be a decimal", { type: "error" });
          return;
        }
        if (value > this.getActiveHotel().meta?.totalRooms) {
          toast("Left to sell cannot be greater than total number of rooms", {
            type: "error",
          });
          return;
        }
      }
    }
    const updateBase = {
      hotelId: this.props.hotel.hotelId,
      start: formatISO(this.props.start, { representation: "date" }),
      end: formatISO(this.props.end, { representation: "date" }),
      updates: this.leftToSellUpdates,
    };
    this.leftToSellUpdates = {};
    YieldApi.updateLeftToSellList(updateBase)
      .catch((err: any) => {
        console.log(err);
        toast.error("Error updating pickup");
      })
      .finally(() => {
        this.props.onDataUpdate();
      });
  };

  getActiveHotel = () => {
    return this.state.hotel;
  };

  render() {
    const { permissions, sheet } = this.props;
    (window as any).sheet = sheet;
    // Deep copy permissions from Redux props to avoid mutation issues
    const perms = {
      isReadOnly: !hasPermission(ACTIONS.PUBLISH),
      canEdit: hasPermission(ACTIONS.PUBLISH),
    };

    const dates = sheet.map((day) => new Date(day.dateMidday));
    console.log("dates", dates);
    const displayRange =
      sheet.length > 0
        ? {
            start: getTime(sheet[0].hrDate),
            end: getTime(sheet[sheet.length - 1].hrDate),
          }
        : {};
    const self = this;
    console.log("yieldsheet data", sheet, this.props.rowProps.rtConf);
    const isOWSEnabled =
      this.state.subscribedSource &&
      this.state.subscribedSource.includes("branddotcom");
    console.log(
      "[YieldSheet.render] isOWSEnabled:",
      isOWSEnabled,
      "subscribedSource:",
      this.state.subscribedSource,
    );
    const hotTableProps: HotTableProps = {
      renderAllRows: true,
      data: this.props.data,
      cells:
        sheet && cells(this.state.hotel, perms, this.props.rowProps, sheet),
      columns: columns(
        displayRange,
        this.state.hotel,
        sheet,
        this.props.compset,
        this.props.compareOtaRate,
        this.props.compareDemand,
        this.props.rowProps,
        this.openSuggestionModal,
        () => {
          this.props.onDataUpdate();
        },
        this.computeLeftToSell,
        isOWSEnabled,
      ),
      afterChange(changes: CellChange[]) {
        self.handleEdit(changes);
      },

      contextMenu: hasPermission(ACTIONS.YIELD_SHEET)
        ? {
            items: getRestrictionsMenuItems(
              self,
              this.props.rowProps,
              perms,
              auth.isReadOnly(perms, this.state.hotel.hotelId),
              this.props.activeHotel,
            ),
          }
        : undefined,
      beforeOnCellMouseDown: function (e: any) {
        if (
          e.target.textContent.includes("Left to Sell") ||
          e.target.textContent.includes("Apply") ||
          e.target.textContent.includes("Clear")
        ) {
          e.stopImmediatePropagation();
        }
      },

      rowHeaders: false,
      stretchH: "all",
      colWidths: colWidths(sheet.length),
      preventOverflow: true,
      fixedColumnsLeft: 1,
      zIndex: 0,
      rowHeights: 35,
      hiddenRows: {
        rows: this.props.rowProps.hiddenRows,
        indicators: true,
      },

      manualColumnResize: false,
      trimColumns: true,
      autoRowSize: false,
      viewportColumnRenderingOffset: 100,
    } as HotTableProps; // this is needed because nestedRows is experimental and not yet in the official typings
    return (
      <section
        className="fullSection"
        style={{
          overflow: "visible",
          zIndex: 0,
          position: "relative",
        }}
      >
        <div
          style={{
            width: "fit-content",
            zIndex: 1,
            position: "sticky",
            top: 130,
          }}
        >
          <EventTable
            key={sheet[0].dateMidday + sheet[sheet.length - 1].dateMidday}
            {...{
              dates: dates,
              hotel: this.state.hotel,
              ys: this,
            }}
          />
        </div>

        <div
          style={{
            width: "fit-content",
            zIndex: 0,
            position: "relative",
            // bottom: 20,
            // paddingTop: "305px",
            marginTop: "-10px",
          }}
        >
          <HotTable ref={this.hotInstanceRef} {...hotTableProps} />
        </div>
        <MatrixModal
          ref={this.suggestionModalInstanceRef}
          hotel={this.state.hotel}
        />
      </section>
    );
  }
}

export { YieldSheet };
